import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import {
	Checkbox,
	Divider,
	InputAdornment,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material';
import Ajv from 'ajv';
import {
	Control,
	Controller,
	Resolver,
	useFieldArray,
	UseFieldArrayUpdate,
	useForm,
	useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { FormContainer, NumericTextField } from '~components';
import { useFormContainerState } from '~hooks';
import { FormWrapperRefProps } from '~interfaces/refProps';
import { getAjvErrorsForReactHookForms } from '~lib/reactHookForm';
import { buildAdyenLogoSvgUrl } from '~utils';
import { datetimeNoSecondsFormatter } from '~utils/dateUtils';

import schema from './refundOneOffPaymentsSchema.json';
import PaymentStatusEnum from '../../enums/paymentStatusEnum';
import Payment from '../../interfaces/payment';
import { RefundNew } from '../../interfaces/refund';
import OneOffPaymentsService from '../../services/oneOffPaymentsService';

const ajv = new Ajv();

const service = new OneOffPaymentsService();

interface RefundPaymentData extends RefundNew {
	oneOffPaymentId: string;
	isPartial: boolean;
}

interface RefundOneOffPaymentsFormProps {
	bookingId: string;
	onSubmit?: (value: RefundPaymentData[]) => void;
	totalBookingAmount: number;
}

const RefundOneOffPaymentsForm = forwardRef<FormWrapperRefProps, RefundOneOffPaymentsFormProps>(
	({ bookingId, onSubmit, totalBookingAmount }, ref) => {
		const { t } = useTranslation();
		const theme = useTheme();

		const { data: oneOffPaymentsData, isLoading: isOneOffPaymentsLoading } = useSWRImmutable(
			bookingId ?
				[service.basePath, { bookingId: bookingId, status: PaymentStatusEnum.Paid }]
			:	null,
			([_, args]) => service.getOneOffPayments(args),
			{
				revalidateOnMount: true,
			}
		);

		/**
		 * May seems like bit of an overkill to validate one field. But the
		 * principle can be request if we want to combine/chain client and server
		 * side validation.
		 * @param values
		 * @param context
		 * @returns
		 */
		const customResolver: Resolver<{ refunds: RefundPaymentData[] }> = async (values) => {
			// First validate the value itself
			let isValid = ajv.validate(schema, values);
			if (!isValid) {
				return getAjvErrorsForReactHookForms(ajv);
			}

			let oneOffPayment: Payment | undefined;
			for (const refund of values.refunds) {
				oneOffPayment = oneOffPaymentsData?.results.find((el) => refund.oneOffPaymentId === el.id);

				isValid = oneOffPayment != null && refund.total <= oneOffPayment.refundableTotal;
				if (isValid) {
					continue;
				}

				return {
					values: {},
					errors: {
						todo: {
							type: 'custom',
							message: 'todo',
						},
					},
				};
			}

			return {
				values: values,
				errors: {},
			};
		};

		const { setDisabled } = useFormContainerState();

		const { getValues, control, formState, watch } = useForm<{
			refunds: RefundPaymentData[];
		}>({
			defaultValues: { refunds: [] },
			mode: 'onChange',
			resolver: customResolver,
		});
		const { append, remove, update } = useFieldArray({
			control: control,
			name: 'refunds',
		});

		useImperativeHandle(
			ref,
			() => ({
				onSubmit: () => onSubmit?.(getValues().refunds),
			}),
			[onSubmit, getValues]
		);

		useEffect(() => {
			setDisabled?.(!formState.isValid);
		}, [formState.isValid, setDisabled]);

		const watchRefunds = watch('refunds');

		// TODO: in useMemo for better performance?
		const totalRefund = watchRefunds.reduce((acc, refund) => (acc += refund.total), 0);
		const newTotal = totalBookingAmount - totalRefund;

		return (
			<FormContainer loading={isOneOffPaymentsLoading}>
				<Typography variant='body2'>
					{t('views.bookings.bookingDetails.refundDialog.description')}
				</Typography>
				<TableContainer>
					<Table
						sx={{
							px: 0,
							[`& .${tableCellClasses.root}`]: {
								borderBottom: 'none',
								'&:first-child': {
									pl: 0,
								},
								'&:last-child': {
									pr: 0,
								},
							},
						}}
					>
						<TableHead>
							<TableRow
								sx={{
									'& .MuiTableCell-head': {
										color: theme.palette.grey[600],
										backgroundColor: 'white',
										fontSize: 12,
										py: 1,
									},
								}}
							>
								<TableCell></TableCell>
								<TableCell>{t('ui.label.paymentMethod')}</TableCell>
								<TableCell>{t('views.devices.heartbeatsList.lastUpdated')}</TableCell>
								<TableCell>{`${t('maximum')} (${t('ui.inclVat')})`}</TableCell>
								<TableCell>{t('ui.label.partialRefund')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{oneOffPaymentsData?.results.map((oneOffPayment) => {
								const refundIndex = watchRefunds.findIndex(
									(el) => el.oneOffPaymentId === oneOffPayment.id
								);
								const refund = watchRefunds.find((el) => el.oneOffPaymentId === oneOffPayment.id);

								return (
									<React.Fragment key={oneOffPayment.id}>
										<TableRow>
											<TableCell padding='checkbox'>
												<Checkbox
													value={refund != null}
													onChange={(_, checked) =>
														checked ?
															append({
																oneOffPaymentId: oneOffPayment.id,
																isPartial: false,
																total: oneOffPayment.refundableTotal,
																vatPercentage: oneOffPayment.vatPercentage,
															})
														: refundIndex >= 0 ? remove(refundIndex)
														: null
													}
												/>
											</TableCell>
											<TableCell>
												<Stack spacing={1} direction='row' sx={{ height: 1, alignItems: 'center' }}>
													{oneOffPayment.paymentMethodType != null && (
														<img
															src={buildAdyenLogoSvgUrl(oneOffPayment.paymentMethodType)}
															style={{ maxWidth: 35, objectFit: 'cover' }}
														/>
													)}
													<div>*** {oneOffPayment.primaryAccountNumber?.truncated}</div>
												</Stack>
											</TableCell>
											<TableCell>
												{datetimeNoSecondsFormatter.format(oneOffPayment.datePaid)}
											</TableCell>
											<TableCell>{`€${oneOffPayment.refundableTotal.toFixed(2)}`}</TableCell>
											<TableCell>
												<ConditionalSwitch
													index={refundIndex}
													control={control}
													update={update}
													refundableTotal={oneOffPayment.refundableTotal}
												/>
											</TableCell>
										</TableRow>
										<ConditionalNumberFieldRow index={refundIndex} control={control} />
									</React.Fragment>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<Stack spacing={1}>
					<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
						<Typography variant='body2'>{`${t('ui.label.currentTotal')} (${t('ui.inclVat')})`}</Typography>
						<Typography variant='body2'>{`€${totalBookingAmount.toFixed(2)}`}</Typography>
					</Stack>
					<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
						<Typography variant='body2'>{`${t('ui.refund')} (${t('ui.inclVat')})`}</Typography>
						<Typography variant='body2'>{`€${totalRefund.toFixed(2)}`}</Typography>
					</Stack>
					<Divider />
					<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
						<Typography variant='body2'>{`${t('ui.label.newTotal')} (${t('ui.inclVat')})`}</Typography>
						<Typography variant='body2'>{`€${newTotal.toFixed(2)}`}</Typography>
					</Stack>
				</Stack>
			</FormContainer>
		);
	}
);

const ConditionalSwitch = ({
	control,
	index,
	update,
	refundableTotal,
}: {
	control: Control<{ refunds: RefundPaymentData[] }>;
	index: number;
	update?: UseFieldArrayUpdate<{ refunds: RefundPaymentData[] }>;
	refundableTotal?: number;
}) => {
	const output = useWatch({
		name: 'refunds',
		control: control,
	});

	if (output[index] == null) {
		return <Switch disabled />;
	}

	const handleChange = (checked: boolean, onChange: (...event: any[]) => void) => {
		if (!checked && refundableTotal) {
			// Reset total to max amount
			const newValue = { ...output[index], total: refundableTotal };
			update?.(index, newValue);
		}

		onChange(checked);
	};

	return (
		<Controller
			name={`refunds.${index}.isPartial`}
			control={control}
			render={({ field }) => (
				<Switch
					checked={field.value}
					onChange={(_, checked) => handleChange(checked, field.onChange)}
				/>
			)}
		/>
	);
};

const ConditionalNumberFieldRow = ({
	control,
	index,
}: {
	control: Control<{ refunds: RefundPaymentData[] }>;
	index: number;
}) => {
	const output = useWatch({
		name: 'refunds',
		control: control,
	});

	if (!output[index]?.isPartial) {
		return null;
	}

	return (
		<TableRow>
			<TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} />
			<TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
				<Controller
					name={`refunds.${index}.total`}
					control={control}
					render={({ field, fieldState, formState }) => (
						<NumericTextField
							value={field.value}
							onValueChange={(values) => field.onChange(values.floatValue)}
							size='small'
							fixedDecimalScale={!fieldState.isDirty}
							slotProps={{
								input: {
									startAdornment: <InputAdornment position='start'>€</InputAdornment>,
								},
							}}
							error={!formState.isValid}
							sx={{ maxWidth: 120, justifyContent: 'flex-end', pr: 0, mr: 0 }}
						/>
					)}
				/>
			</TableCell>
		</TableRow>
	);
};

export default RefundOneOffPaymentsForm;
